/**
 * Date of birth as typed text (MM/DD/YYYY), not a native date picker.
 *
 * iOS opens an empty <input type="date"> on today's date. Scrolling the month
 * to one with fewer days clamps the day (Oct 31 → Jun 30), and a controlled
 * React value can snap the picker back to that clamped day while the customer
 * corrects it. A traveler booked 10-31 (CPDUIJ) went through as 6/30/1980
 * instead of 6/23/1980 that way. Typed digits have no picker to fight with.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const daysInMonth = (year, month) => new Date(Date.UTC(year, month, 0)).getUTCDate();

/** '1980-06-23' (or a timestamp starting with it) → '06/23/1980'; anything else → ''. */
export function isoToMdy(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
}

const monthFromName = (name) => {
  const i = MONTHS.findIndex((m) => m.slice(0, 3).toLowerCase() === String(name).slice(0, 3).toLowerCase());
  return i === -1 ? '' : String(i + 1).padStart(2, '0');
};
const SEPARATOR = /[/.\-\s,]/;

/**
 * What the field shows for what was typed, pasted or autofilled.
 *
 * Whole dates in other shapes become MM/DD/YYYY: '1980-06-23', '1980/6/23',
 * 'June 23, 1980', '23 Jun 1980'.
 *
 * Typing fills month, day, year in turn. A field moves on when it's full
 * (a slash appears once the next digit arrives, so '06' stays '06') or when
 * the customer types a separator, which pads what they typed: '6/' → '06/',
 * '06/3/' → '06/03/'. That's how people type dates on a keyboard; reading
 * '6/23/1980' digit by digit gave '62/31/980'. Backspacing over a slash just
 * removes it ('06/' → '06').
 */
export function formatMdyInput(raw) {
  const s = String(raw || '').trim();
  const ymd = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/.exec(s);
  if (ymd) return `${ymd[2].padStart(2, '0')}/${ymd[3].padStart(2, '0')}/${ymd[1]}`;
  const named = /^([a-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/i.exec(s);
  if (named && monthFromName(named[1])) return `${monthFromName(named[1])}/${named[2].padStart(2, '0')}/${named[3]}`;
  const dayNamed = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]{3,9})\.?,?\s+(\d{4})$/i.exec(s);
  if (dayNamed && monthFromName(dayNamed[2])) return `${monthFromName(dayNamed[2])}/${dayNamed[1].padStart(2, '0')}/${dayNamed[3]}`;

  const fields = ['', '', ''];
  let at = 0;
  for (const ch of s) {
    if (/\d/.test(ch)) {
      if (at < 2 && fields[at].length === 2) at += 1;
      if (at === 2 && fields[2].length === 4) break;
      fields[at] += ch;
    } else if (SEPARATOR.test(ch) && at < 2 && fields[at]) {
      fields[at] = fields[at].padStart(2, '0');
      at += 1;
    }
  }
  return [fields[0], at >= 1 ? `/${fields[1]}` : '', at >= 2 ? `/${fields[2]}` : ''].join('');
}

/**
 * Why a date that isn't done yet can't be used, for when the customer leaves
 * the field (while typing, an unfinished date isn't an error). '' when the
 * text is empty or a whole date.
 */
export function incompleteDobHint(text) {
  const t = String(text || '');
  if (!t || /^\d{2}\/\d{2}\/\d{4}$/.test(t)) return '';
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(t)) return 'Use the full year, like 1980.';
  return 'Enter the full date as MM/DD/YYYY.';
}

/**
 * Parse 'MM/DD/YYYY'. Returns { iso, error }:
 * - incomplete or mid-edit input: { iso: '', error: '' } (not an error yet, just not done)
 * - complete and valid: { iso: 'YYYY-MM-DD', error: '' }
 * - complete but impossible: { iso: '', error: '<what to fix>' }
 */
export function parseMdy(text, { today = new Date() } = {}) {
  // Only the exact shape counts. A date mid-edit ('06/3/1980') is not done,
  // and reading its digits straight through would save the wrong day.
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(String(text || ''))) return { iso: '', error: '' };
  const d = String(text).replace(/\D/g, '');
  const month = Number(d.slice(0, 2));
  const day = Number(d.slice(2, 4));
  const year = Number(d.slice(4, 8));
  if (month > 12 && day >= 1 && day <= 12) return { iso: '', error: 'Month comes first: MM/DD/YYYY.' };
  if (month < 1 || month > 12) return { iso: '', error: 'Check the month (01–12).' };
  if (year < 1900) return { iso: '', error: 'Check the year.' };
  if (day < 1 || day > daysInMonth(year, month)) {
    return { iso: '', error: `${MONTHS[month - 1]} ${year} doesn't have a day ${day}.` };
  }
  const iso = `${d.slice(4, 8)}-${d.slice(0, 2)}-${d.slice(2, 4)}`;
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  if (iso > todayIso) return { iso: '', error: "A date of birth can't be in the future." };
  return { iso, error: '' };
}

/** '1980-06-23' → 'June 23, 1980' (no time zones involved); '' when not a date. */
export function dobInWords(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const month = Number(m[2]);
  if (month < 1 || month > 12) return '';
  return `${MONTHS[month - 1]} ${Number(m[3])}, ${m[1]}`;
}
